/**
 * Canonical enums / string-literal unions for the Agentic Operating Core.
 *
 * THIS FILE IS THE AS-BUILT SINGLE SOURCE OF TRUTH for every shared enum the
 * worker, schemas, and portal consume (V3 impl-plan S1; technical/04 §8.3,
 * technical/07, technical/11). Downstream slices import the tuples from here
 * rather than re-declaring values. All changes are APPEND-ONLY where index
 * stability matters (statuses, effect types, event types) so existing TEXT data
 * never breaks. V3 effect names are canonical; v0.1 names are compile-time
 * aliases (technical/04 §8.3 / OQ-26).
 *
 * Each enum is exported as a `readonly` tuple (`X_VALUES`) so the worker can
 * validate against it (via `z.enum`) and the portal can build dropdowns, plus a
 * union type (`X`) inferred from the tuple. Mirrors IMPLEMENTATION_CONTRACT §4.2.
 */

export const ARTIFACT_TYPES = [
  'task',
  'project',
  'document',
  'decision',
  'risk',
  'objective',
  'key_result',
  'metric',
  'measurement',
  'evidence',
  'skill',
  'process',
  'rule',
  'agent_run',
  'pricing_signal',
  'site_opportunity',
  'lead',
  'feasibility_case',
  'package_improvement_proposal',
  'deliverable',
  'outcome_record',
  'issue',
  'milestone',
] as const;
export type ArtifactType = (typeof ARTIFACT_TYPES)[number];

export const EVENT_TYPES = [
  // --- v0.1 (12 values; keep FIRST so index stability + TRIGGER_EVENT_TYPES hold) ---
  'artifact.created',
  'artifact.updated',
  'task.created',
  'task.updated',
  'skill.published',
  'evidence.created',
  'decision.made',
  'metric.measured',
  'agent_run.created',
  'agent_run.completed',
  'agent_run.failed_validation',
  'document.linked',
  'timer.fired',
  // --- v0.2 additions (CONTRACT_V2 §4.1; appended, never reorder the above) ---
  'signal.created',
  'signal.triaged',
  'context.created',
  'decision.requested',
  'transition.proposed',
  'package.published',
  'package.deployed',
  'package.deployment_paused',
  'package.deployment_resumed',
  'package.deployment_archived',
  'package.workspace_created',
  'package.usage_rolled_up',
  'package.improvement_proposed',
  'package.version_published',
  'package.deployment_upgraded',
  'timer.created',
  // --- Starter-contour runtime additions (CONTOUR-A-CONTRACT §4.5; appended) ---
  'contour.instance_started',
  'transition.executed',
  'agent_profile.created',
  'agent_profile.version_created',
  'agent_profile.version_promoted',
  'agent_binding.enabled',
  'agent_binding.paused',
  'agent_binding.revoked',
  'agent_harness.passed',
  'agent_harness.waived',
  'tool_invocation.requested',
  'tool_invocation.completed',
  'tool_invocation.blocked',
  'access.denied',
  'access.redacted',
  'access.boundary_changed',
  'access.policy_changed',
  'access.role_assignment_checked',
  'approval.requested',
  'approval.approved',
  'approval.rejected',
  'approval.changes_requested',
  'approval.expired',
  'credential_grant.created',
  'credential_grant.revoked',
  'credential.expired',
  'credential.rotated',
  'tool.invocation_requested',
  'tool.invoked',
  'tool.failed',
  'context_packet.redacted',
  'action_card.proposed',
  'action_card.updated',
  'action_card.accepted',
  'action_card.rejected',
  'action_card.deferred',
  'action_card.reactivated',
  'action_card.completed',
  'action_card.failed',
  'action_card.expired',
  'action_card.superseded',
  'action_card.cancelled',
  // --- Contour runtime + crystallization (technical/12 §6.3/§7; S11; appended) ---
  'contour.instance_completed',
  'artifact.transitioned',
  // --- Claim acceptance ladder (technical/11 claim.* events / OQ-25 / K3;
  // appended) — emitted by claim-service as a claim crosses its lifecycle, and
  // surfaced on the decision-room timeline. Appended, never reordered. ---
  'claim.asserted',
  'claim.review_requested',
  'claim.accepted',
  'claim.rejected',
  'claim.contradicted',
  'claim.superseded',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

/**
 * Triggers are the subset of event types a rule may listen to.
 * In v0.1 this is exactly the full set of event types.
 */
export const TRIGGER_EVENT_TYPES = EVENT_TYPES;
export type TriggerEventType = EventType;

export const CONDITION_TYPES = [
  'artifact_type_equals',
  'status_equals',
  'phase_equals',
  'evidence_exists',
  'evidence_count_gte',
  'decision_exists',
  'metric_above',
  'metric_below',
] as const;
export type ConditionType = (typeof CONDITION_TYPES)[number];

export const EFFECT_TYPES = [
  // --- v0.1 (8 values; keep FIRST) ---
  'create_task',
  'update_artifact',
  'create_evidence',
  'create_decision_request',
  'dispatch_skill',
  'send_teams_message',
  'block',
  'transition_status',
  // --- v0.2 additions (CONTRACT_V2 §4.2; appended) ---
  'transition_phase',
  'create_review',
  'create_proposal',
  'update_score',
  // --- Starter-contour runtime addition (CONTOUR-A-CONTRACT §4.2; appended) ---
  'dispatch_contour',
  'create_action_card',
  // --- V3 canonical effect names (technical/04 §8.3 / OQ-26; appended) ---
  // dispatch_agent / request_decision / invoke_tool reuse the legacy runtime
  // cases via EFFECT_TYPE_ALIASES; request_approval / notify_internal are new.
  'dispatch_agent',
  'request_decision',
  'invoke_tool',
  'request_approval',
  'notify_internal',
] as const;
export type EffectType = (typeof EFFECT_TYPES)[number];

/**
 * technical/04 §8.3 — V3 canonical effect name → v0.1 runtime case. A rule that
 * authors a V3 name compiles down to its legacy alias so no runtime case is
 * duplicated. `request_approval` and `notify_internal` are new V3-only cases
 * with no legacy alias (they map to themselves).
 */
export const EFFECT_TYPE_ALIASES = {
  dispatch_agent: 'dispatch_skill',
  request_decision: 'create_decision_request',
  invoke_tool: 'send_teams_message',
  request_approval: 'request_approval',
  notify_internal: 'notify_internal',
} as const satisfies Record<string, EffectType>;
export type EffectTypeAlias = keyof typeof EFFECT_TYPE_ALIASES;

export const EXECUTOR_TYPES = ['worker', 'ai_api', 'github_actions', 'cloudflare_agents', 'external', 'vps'] as const;
export type ExecutorType = (typeof EXECUTOR_TYPES)[number];

export const ROLES = [
  'admin',
  'management',
  'department_manager',
  'project_manager',
  'contributor',
  'viewer',
  'agent_service',
] as const;
export type RoleName = (typeof ROLES)[number];

export const CONFIDENTIALITY = ['public', 'internal', 'confidential'] as const;
export type Confidentiality = (typeof CONFIDENTIALITY)[number];

// --- status families (string-literal unions). Narrow, but include DB defaults. ---

/** companies, projects, objectives, key_results, processes */
export const ENTITY_STATUS = ['active', 'inactive', 'archived'] as const;
export type EntityStatus = (typeof ENTITY_STATUS)[number];

export const USER_STATUS = ['active', 'inactive', 'suspended'] as const;
export type UserStatus = (typeof USER_STATUS)[number];

export const ARTIFACT_STATUS = ['new', 'in_progress', 'blocked', 'done', 'cancelled'] as const;
export type ArtifactStatus = (typeof ARTIFACT_STATUS)[number];

export const TASK_STATUS = ['new', 'in_progress', 'blocked', 'done', 'cancelled'] as const;
export type TaskStatus = (typeof TASK_STATUS)[number];

export const SKILL_STATUS = ['draft', 'review', 'published', 'archived'] as const;
export type SkillStatus = (typeof SKILL_STATUS)[number];

export const RULE_STATUS = ['draft', 'active', 'disabled', 'dry_run'] as const;
export type RuleStatus = (typeof RULE_STATUS)[number];

export const EVENT_STATUS = ['new', 'processing', 'processed', 'failed', 'non_replayable'] as const;
export type EventStatus = (typeof EVENT_STATUS)[number];

/**
 * Milestone A (canonical 06 §3.1; IMPL-A-CONTRACT §1.4) — closed taxonomy of
 * event producers, stored on `event_sources.source_type` and mirrored onto
 * `events.source_type`. `file_watcher` / `manual_import` are reserved for later
 * connectors (no seed row).
 */
export const EVENT_SOURCE_TYPES = [
  'ui_action',
  'domain_service',
  'timer',
  'agent_callback',
  'connector_webhook',
  'connector_poll',
  'file_watcher',
  'manual_import',
  'system_monitor',
] as const;
export type EventSourceType = (typeof EVENT_SOURCE_TYPES)[number];

export const RULE_RUN_STATUS = ['running', 'success', 'failed', 'skipped', 'dry_run'] as const;
export type RuleRunStatus = (typeof RULE_RUN_STATUS)[number];

/**
 * technical/04 §8.2 — outcome of a single effect execution, stored on the
 * `effect_runs` rows S2 introduces. Mirrors `RULE_RUN_STATUS` (effects run, then
 * succeed, fail, or are skipped when policy/idempotency short-circuits them).
 */
export const EFFECT_RUN_STATUS = ['running', 'success', 'failed', 'skipped'] as const;
export type EffectRunStatus = (typeof EFFECT_RUN_STATUS)[number];

/**
 * technical/04 — lifecycle of an outbox row in the event outbox S2 introduces:
 * `pending` until the publisher drains it, `published` once delivered to the
 * queue, `failed` after exhausted retries (DLQ candidate).
 */
export const EVENT_OUTBOX_STATUS = ['pending', 'published', 'failed'] as const;
export type EventOutboxStatus = (typeof EVENT_OUTBOX_STATUS)[number];

export const AGENT_RUN_STATUS = [
  // --- v0.1 built statuses (5; keep FIRST — index stability) ---
  'queued',
  'running',
  'completed',
  'failed',
  'cancelled',
  // --- Milestone A additions (canonical 09 §10.1; appended, never reorder the above) ---
  'dispatching',
  'waiting_callback',
  'validating_output',
  'failed_validation',
  'timed_out',
] as const;
export type AgentRunStatus = (typeof AGENT_RUN_STATUS)[number];

/**
 * Milestone A (canonical 09 §7) — outcomes of the AgentOutputValidator, stored on
 * `agent_runs.output_validation_status`.
 */
export const OUTPUT_VALIDATION_STATUSES = [
  // --- 7-member stored set (technical/07 lines 109-119; keep FIRST) ---
  'accepted',
  'accepted_with_warnings',
  'needs_human_review',
  'failed_schema',
  'failed_policy',
  'failed_access',
  'failed_confidence',
  // --- V3 target extensions (technical/07 lines 128-136; appended) ---
  // [PROPOSED] finer-grained reasons. They MUST NOT be stored on
  // `agent_runs.output_validation_status` until migration S3 lands the column;
  // until then callers fold them to the 7-member set via OUTPUT_VALIDATION_FOLD.
  'failed_contract',
  'failed_redaction',
  'failed_unknown_subject',
] as const;
export type OutputValidationStatus = (typeof OUTPUT_VALIDATION_STATUSES)[number];

/**
 * The 7-member subset that is safe to STORE on
 * `agent_runs.output_validation_status` today (before the S3 migration).
 */
export const OUTPUT_VALIDATION_STORED_STATUSES = [
  'accepted',
  'accepted_with_warnings',
  'needs_human_review',
  'failed_schema',
  'failed_policy',
  'failed_access',
  'failed_confidence',
] as const;
export type OutputValidationStoredStatus = (typeof OUTPUT_VALIDATION_STORED_STATUSES)[number];

/**
 * technical/07 lines 121-126 — fold each finer-grained target-extension reason
 * to the stored 7-member value the validator persists today: contract failures
 * fold under `failed_schema`, redaction failures under `failed_policy`, and
 * out-of-context (unknown-subject) failures under `failed_access`. The seven
 * stored values map to themselves so callers can fold any status uniformly.
 */
export const OUTPUT_VALIDATION_FOLD = {
  accepted: 'accepted',
  accepted_with_warnings: 'accepted_with_warnings',
  needs_human_review: 'needs_human_review',
  failed_schema: 'failed_schema',
  failed_policy: 'failed_policy',
  failed_access: 'failed_access',
  failed_confidence: 'failed_confidence',
  failed_contract: 'failed_schema',
  failed_redaction: 'failed_policy',
  failed_unknown_subject: 'failed_access',
} as const satisfies Record<OutputValidationStatus, OutputValidationStoredStatus>;

/**
 * technical/07 line 45 — the AgentOutput envelope `run_status`. This is the
 * executor-reported outcome carried in the envelope, distinct from
 * `AGENT_RUN_STATUS` (the lifecycle state stored on `agent_runs.status`).
 */
export const AGENT_OUTPUT_RUN_STATUS = ['completed', 'partial', 'blocked', 'failed'] as const;
export type AgentOutputRunStatus = (typeof AGENT_OUTPUT_RUN_STATUS)[number];

export const AGENT_PROFILE_KINDS = ['assistant', 'worker'] as const;
export type AgentProfileKind = (typeof AGENT_PROFILE_KINDS)[number];

export const AGENT_PROFILE_SPECIALIZATIONS = [
  'user_assistant',
  'context_assistant',
  'verifier',
  'connector',
  'scheduler',
  'script',
  'code_sandbox',
  'research',
  'classifier',
  'monitor',
  'deterministic_transform',
] as const;
export type AgentProfileSpecialization = (typeof AGENT_PROFILE_SPECIALIZATIONS)[number];

export const AGENT_PROFILE_STATUS = [
  'draft',
  'test',
  'active',
  'suspended',
  'deprecated',
  'archived',
] as const;
export type AgentProfileStatus = (typeof AGENT_PROFILE_STATUS)[number];

export const AGENT_PROFILE_VERSION_STATUS = [
  'draft',
  'test',
  'promoted',
  'retired',
] as const;
export type AgentProfileVersionStatus = (typeof AGENT_PROFILE_VERSION_STATUS)[number];

export const AGENT_BINDING_STATUS = [
  'planned',
  'enabled',
  'paused',
  'expired',
  'revoked',
] as const;
export type AgentBindingStatus = (typeof AGENT_BINDING_STATUS)[number];

export const AGENT_ACTIVATION_KINDS = ['chat', 'event', 'timer', 'manual', 'callback'] as const;
export type AgentActivationKind = (typeof AGENT_ACTIVATION_KINDS)[number];

export const AGENT_ACTIVATION_SOURCES = [
  'chat_session',
  'rule_effect',
  'event_hook',
  'connector_webhook',
  'connector_poll',
  'contour_transition',
  'scheduled_diagnostic',
  'action_card',
  'external_callback',
] as const;
export type AgentActivationSource = (typeof AGENT_ACTIVATION_SOURCES)[number];

export const AGENT_HARNESS_RUN_STATUS = ['running', 'passed', 'failed', 'waived', 'cancelled'] as const;
export type AgentHarnessRunStatus = (typeof AGENT_HARNESS_RUN_STATUS)[number];

export const TOOL_INVOCATION_STATUS = [
  'requested',
  'checking_access',
  'awaiting_confirmation',
  'awaiting_approval',
  'invoking',
  'denied',
  'succeeded',
  'failed',
  'cancelled',
  'completed',
  'blocked',
] as const;
export type ToolInvocationStatus = (typeof TOOL_INVOCATION_STATUS)[number];

export const DECISION_STATUS = ['requested', 'approved', 'rejected', 'cancelled'] as const;
export type DecisionStatus = (typeof DECISION_STATUS)[number];

export const PRIORITY = ['low', 'medium', 'high', 'urgent'] as const;
export type Priority = (typeof PRIORITY)[number];

/** RBAC actions (used by `can()` and the access provider). */
export const ACTIONS = [
  'list',
  'show',
  'create',
  'edit',
  'delete',
  'approve',
  'replay',
  'retry',
  'dispatch',
] as const;
export type Action = (typeof ACTIONS)[number];

export const ACTOR_TYPES = [
  'human',
  'assistant',
  'worker',
  'service',
  'team',
  'department',
  'company',
  'committee',
  'external_stakeholder',
  'external_system',
] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

export const ACTOR_STATUS = ['active', 'inactive', 'suspended', 'archived'] as const;
export type ActorStatus = (typeof ACTOR_STATUS)[number];

export const IDENTITY_STATUS = ['active', 'inactive', 'suspended', 'revoked'] as const;
export type IdentityStatus = (typeof IDENTITY_STATUS)[number];

export const ACTOR_MEMBERSHIP_TYPES = [
  'member',
  'owner',
  'manager',
  'representative',
  'service_account',
] as const;
export type ActorMembershipType = (typeof ACTOR_MEMBERSHIP_TYPES)[number];

export const AUTHORITY_STATUSES = ['active', 'inactive', 'revoked', 'expired'] as const;
export type AuthorityStatus = (typeof AUTHORITY_STATUSES)[number];

export const ROLE_ASSIGNMENT_SOURCE_TYPES = [
  'direct',
  'legacy_user_role',
  'membership',
  'delegation',
  'system',
] as const;
export type RoleAssignmentSourceType = (typeof ROLE_ASSIGNMENT_SOURCE_TYPES)[number];

export const ACTION_POLICY_RISK_LEVELS = ['low', 'medium', 'high', 'critical'] as const;
export type ActionPolicyRiskLevel = (typeof ACTION_POLICY_RISK_LEVELS)[number];

export const APPROVAL_MODES = [
  'auto_allowed',
  'confirmation_required',
  'approval_required',
  'human_only',
  'forbidden',
] as const;
export type ApprovalMode = (typeof APPROVAL_MODES)[number];

export const APPROVAL_REQUEST_STATUS = [
  'pending',
  'approved',
  'rejected',
  'cancelled',
  'expired',
  'executed',
] as const;
export type ApprovalRequestStatus = (typeof APPROVAL_REQUEST_STATUS)[number];

export const APPROVAL_DECISIONS = ['approved', 'rejected', 'cancelled'] as const;
export type ApprovalDecision = (typeof APPROVAL_DECISIONS)[number];

export const ACTION_CARD_STATUSES = [
  'proposed',
  'accepted',
  'completed',
  'failed',
  'rejected',
  'deferred',
  'expired',
  'superseded',
  'cancelled',
] as const;
export type ActionCardStatus = (typeof ACTION_CARD_STATUSES)[number];

export const ACTION_CARD_OPEN_STATUSES = [
  'proposed',
  'deferred',
  'accepted',
  'failed',
] as const;
export type ActionCardOpenStatus = (typeof ACTION_CARD_OPEN_STATUSES)[number];

export const ACTION_CARD_TYPES = [
  'diagnostic',
  'governance',
  'evidence',
  'decision',
  'transition',
  'unblocker',
  'skill_package_improvement',
  'retry',
  'access_credential_fix',
  'crystallization',
  'review',
  'routine_maintenance',
  'informational',
] as const;
export type ActionCardType = (typeof ACTION_CARD_TYPES)[number];

export const ACTION_CARD_CATEGORIES = [
  'missing_owner',
  'stale_review',
  'no_recent_signal',
  'evidence_gap',
  'stalled_decision',
  'blocked_deliverable',
  'transition_bottleneck',
  'agent_validation_failures',
  'access_blocker',
  'duplicate_conflict',
  'crystallization_ready',
  'manual',
] as const;
export type ActionCardCategory = (typeof ACTION_CARD_CATEGORIES)[number];

export const ACTION_CARD_SEVERITIES = ACTION_POLICY_RISK_LEVELS;
export type ActionCardSeverity = (typeof ACTION_CARD_SEVERITIES)[number];

export const ACTION_CARD_GENERATED_BY_TYPES = [
  'rule',
  'agent',
  'timer',
  'user',
  'system',
] as const;
export type ActionCardGeneratedByType = (typeof ACTION_CARD_GENERATED_BY_TYPES)[number];

export const ACTION_CARD_LINK_TYPES = [
  'supports',
  'caused_by',
  'blocks',
  'requests',
  'spawned',
  'supersedes',
  'similar_to',
  'redacted_support',
] as const;
export type ActionCardLinkType = (typeof ACTION_CARD_LINK_TYPES)[number];

export const ACTION_CARD_LINK_VISIBILITY = [
  'normal',
  'redacted',
  'hidden',
  'aggregate_only',
] as const;
export type ActionCardLinkVisibility = (typeof ACTION_CARD_LINK_VISIBILITY)[number];

export const ACTION_CARD_SUBJECT_TYPES = [
  'context',
  'project',
  'artifact',
  'deliverable',
  'task',
  'package',
  'package_version',
  'package_deployment',
  'skill',
  'contour',
  'phase',
  'transition_proposal',
  'claim',
  'decision',
  'signal',
  'agent_run',
  'rule_run',
  'metric',
  'key_result',
  'actor',
  'role_assignment',
  'credential_grant',
  'external_record',
] as const;
export type ActionCardSubjectType = (typeof ACTION_CARD_SUBJECT_TYPES)[number];

export const ACTION_CARD_SUGGESTED_ACTION_TYPES = [
  'assign_owner',
  'schedule_review',
  'inspect_source',
  'adjust_watch',
  'request_evidence',
  'dispatch_verifier',
  'open_review',
  'open_decision',
  'unblock_deliverable',
  'review_transition',
  'retry_agent_run',
  'request_package_improvement',
  'request_role',
  'request_delegation',
  'request_credential_review',
  'merge_duplicate_cards',
  'promote_context',
  'create_task',
  'update_monitoring_profile',
  'dismiss',
  'no_op_review',
] as const;
export type ActionCardSuggestedActionType = (typeof ACTION_CARD_SUGGESTED_ACTION_TYPES)[number];

export const CREDENTIAL_STATUS = ['active', 'inactive', 'revoked', 'rotated'] as const;
export type CredentialStatus = (typeof CREDENTIAL_STATUS)[number];

export const CREDENTIAL_GRANT_STATUS = ['active', 'inactive', 'revoked', 'expired'] as const;
export type CredentialGrantStatus = (typeof CREDENTIAL_GRANT_STATUS)[number];

export const ACCESS_RUNTIME_DECISION_STATES = [
  'denied',
  'auto_allowed',
  'confirmation_required',
  'approval_required',
  'human_only',
  'forbidden',
] as const;
export type AccessRuntimeDecisionState = (typeof ACCESS_RUNTIME_DECISION_STATES)[number];

export const TOOL_BROKER_RESULT_STATES = [
  'denied',
  'awaiting_confirmation',
  'awaiting_approval',
  'human_only',
  'invoked',
  'idempotent_replay',
  'blocked',
] as const;
export type ToolBrokerResultState = (typeof TOOL_BROKER_RESULT_STATES)[number];

// ===========================================================================
// v0.2 — Context Atlas / Signals / Package Graph / Resolution
// (CONTRACT_V2 §4; values taken verbatim from specs/upd-1.md as cited.)
// ===========================================================================

// --- Context atlas ---------------------------------------------------------

/** §5.2 — kinds of context the atlas can hold. */
export const CONTEXT_TYPES = [
  'field',
  'organization',
  'department',
  'project',
  'workflow',
  'phase',
  'strategy',
  'frame',
  'principle',
  'temporal',
  'actor',
  'access',
  'external',
  'package_workspace',
] as const;
export type ContextType = (typeof CONTEXT_TYPES)[number];

/** §7 — context maturity lifecycle stages (ambient field → infrastructure). */
export const CONTEXT_MATURITY_STAGES = [
  'ambient_field',
  'attention_field',
  'signal_field',
  'named',
  'bounded',
  'structured',
  'operational',
  'executable',
  'governed',
  'infrastructure',
] as const;
export type ContextMaturityStage = (typeof CONTEXT_MATURITY_STAGES)[number];

export const CONTEXT_CRYSTALLIZATION_TARGET_TYPES = [
  'project',
  'strategic_bet',
  'contour',
  'frame',
  'principle',
  'skill_package',
  'control_room',
] as const;
export type ContextCrystallizationTargetType =
  (typeof CONTEXT_CRYSTALLIZATION_TARGET_TYPES)[number];

export const CONTEXT_MONITORING_CADENCES = [
  'manual',
  'realtime',
  'hourly',
  'daily',
  'weekly',
  'monthly',
] as const;
export type ContextMonitoringCadence = (typeof CONTEXT_MONITORING_CADENCES)[number];

export const CONTEXT_MONITORING_PROFILE_KINDS = [
  'control_room',
  'default_monitoring',
  'diagnostic_pack',
] as const;
export type ContextMonitoringProfileKind =
  (typeof CONTEXT_MONITORING_PROFILE_KINDS)[number];

export const CONTEXT_ACTION_MODES = [
  'manual_only',
  'suggest_and_approve',
  'auto_low_risk',
  'auto_diagnostic_only',
] as const;
export type ContextActionMode = (typeof CONTEXT_ACTION_MODES)[number];

export const CONTEXT_MONITORING_STATUS = ['proposed', 'active', 'paused', 'archived'] as const;
export type ContextMonitoringStatus = (typeof CONTEXT_MONITORING_STATUS)[number];

export const LENS_MATURITY = ['draft', 'review', 'published', 'deprecated', 'archived'] as const;
export type LensMaturity = (typeof LENS_MATURITY)[number];

export const LENS_VALIDATION_STATUS = ['untested', 'valid', 'invalid'] as const;
export type LensValidationStatus = (typeof LENS_VALIDATION_STATUS)[number];

export const SAVED_VIEW_ROOT_MODES = ['fixed', 'dynamic', 'multi'] as const;
export type SavedViewRootMode = (typeof SAVED_VIEW_ROOT_MODES)[number];

export const SAVED_VIEW_SHARE_SCOPES = ['private', 'shared', 'organization', 'system'] as const;
export type SavedViewShareScope = (typeof SAVED_VIEW_SHARE_SCOPES)[number];

export const SAVED_VIEW_STATUS = ['proposed', 'active', 'archived'] as const;
export type SavedViewStatus = (typeof SAVED_VIEW_STATUS)[number];

export const VIEW_SEMANTIC_LEVELS = ['L0', 'L1', 'L2', 'L3'] as const;
export type ViewSemanticLevel = (typeof VIEW_SEMANTIC_LEVELS)[number];

export const VIEW_LAYOUT_ENGINES = ['cluster-grid', 'dagre', 'force'] as const;
export type ViewLayoutEngine = (typeof VIEW_LAYOUT_ENGINES)[number];

export const VIEW_NODE_KINDS = [
  'context',
  'project',
  'package',
  'artifact',
  'decision',
  'run',
  'cluster',
  'redacted',
] as const;
export type ViewNodeKind = (typeof VIEW_NODE_KINDS)[number];

export const VIEW_EDGE_KINDS = [
  'hosts',
  'parent_of',
  'observes',
  'deploys',
  'constrains',
  'provides_context',
  'prioritizes',
  'lineage',
] as const;
export type ViewEdgeKind = (typeof VIEW_EDGE_KINDS)[number];

export const VIEW_REDACTION_REASONS = [
  'access_denied',
  'sealed_context',
  'confidentiality_ceiling',
  'limit',
] as const;
export type ViewRedactionReason = (typeof VIEW_REDACTION_REASONS)[number];

/** §6.2 — how a bound entity relates to a context. */
export const BINDING_TYPES = [
  'contains',
  'activates',
  'constrains',
  'prioritizes',
  'provides_context',
  'provides_evidence',
  'grants_access',
  'requires',
  'blocks',
  'depends_on',
  'observes',
  'deploys',
  'related_to',
  'originated_in',
] as const;
export type BindingType = (typeof BINDING_TYPES)[number];

/** §5.3 — entity_type domain for context_bindings. */
export const BINDING_ENTITY_TYPES = [
  'artifact',
  'document',
  'signal',
  'package',
  'skill',
  'contour',
  'rule',
  'bet',
  'frame',
  'principle',
  'user',
  'role',
  'agent',
  'context',
  'metric',
  'decision',
  'phase',
  'process',
  'objective',
  'key_result',
] as const;
export type BindingEntityType = (typeof BINDING_ENTITY_TYPES)[number];

/** Access mode on a context_binding (read/write/admin). */
export const ACCESS_MODES = ['read', 'write', 'admin'] as const;
export type AccessMode = (typeof ACCESS_MODES)[number];

export const PACKAGE_DEPLOYMENT_STATUSES = [
  'requested',
  'configured',
  'awaiting_approval',
  'active',
  'paused',
  'archived',
] as const;
export type PackageDeploymentStatus = (typeof PACKAGE_DEPLOYMENT_STATUSES)[number];

export const PACKAGE_DEPLOYMENT_UPGRADE_MODES = [
  'pinned',
  'manual_review',
  'auto_patch',
  'auto_minor',
] as const;
export type PackageDeploymentUpgradeMode = (typeof PACKAGE_DEPLOYMENT_UPGRADE_MODES)[number];

export const PACKAGE_VERSION_STATUSES = [
  'draft',
  'proposed',
  'review',
  'approved',
  'published',
  'rejected',
  'superseded',
  'deferred',
] as const;
export type PackageVersionStatus = (typeof PACKAGE_VERSION_STATUSES)[number];

export const PACKAGE_IMPROVEMENT_RISK_LEVELS = ['low', 'medium', 'high'] as const;
export type PackageImprovementRiskLevel =
  (typeof PACKAGE_IMPROVEMENT_RISK_LEVELS)[number];

export const PACKAGE_USAGE_PRIVACY_VISIBILITY = [
  'aggregate',
  'suppressed',
  'source_context_only',
] as const;
export type PackageUsagePrivacyVisibility =
  (typeof PACKAGE_USAGE_PRIVACY_VISIBILITY)[number];

// --- Signals ---------------------------------------------------------------

/** §9.1 — signal kinds. */
export const SIGNAL_TYPES = [
  'pain',
  'anomaly',
  'opportunity',
  'risk',
  'repeated_question',
  'missing_data',
  'user_request',
  'market_signal',
  'process_failure',
  'agent_failure',
  'context_conflict',
] as const;
export type SignalType = (typeof SIGNAL_TYPES)[number];

/** §9.2 — signal triage lifecycle. */
export const SIGNAL_STATUS = [
  'new',
  'triaged',
  'clustered',
  'candidate_object',
  'converted_to_evidence',
  'converted_to_package_proposal',
  'ignored',
  'archived',
] as const;
export type SignalStatus = (typeof SIGNAL_STATUS)[number];

// --- Packages --------------------------------------------------------------

/** §11 — the five package types stored in the unified `packages` table. */
export const PACKAGE_TYPES = ['skill', 'contour', 'bet', 'frame', 'principle'] as const;
export type PackageType = (typeof PACKAGE_TYPES)[number];

/** §11.2 — abstraction level a package targets. */
export const TARGET_LEVELS = [
  'implementation',
  'orchestration',
  'strategy',
  'framing',
  'principles',
] as const;
export type TargetLevel = (typeof TARGET_LEVELS)[number];

/** §11.2 — package maturity lifecycle. */
export const PACKAGE_MATURITY_STAGES = [
  'raw_fragment',
  'package',
  'structured',
  'operational',
  'executable',
  'infrastructure',
  'deprecated',
] as const;
export type PackageMaturityStage = (typeof PACKAGE_MATURITY_STAGES)[number];

/** §11.3 — typed edges in the package graph. */
export const LINK_TYPES = [
  'uses',
  'contains',
  'supports',
  'constrains',
  'shapes',
  'prioritizes',
  'invokes',
  'evidences',
  'challenges',
  'replaces',
  'derived_from',
] as const;
export type LinkType = (typeof LINK_TYPES)[number];

/** §13.1/§13.2 — what a weak contour does with an unknown transition target. */
export const UNKNOWN_TRANSITION_MODES = ['block', 'create_proposal', 'create_review'] as const;
export type UnknownTransitionMode = (typeof UNKNOWN_TRANSITION_MODES)[number];

// --- Context resolution ----------------------------------------------------

/** §16.1 — how much context to assemble into an agent packet. */
export const CONTEXT_POLICIES = ['minimal', 'standard', 'deep', 'strategic', 'governed'] as const;
export type ContextPolicy = (typeof CONTEXT_POLICIES)[number];

// --- Evidence / claims / crystallization -----------------------------------

/** §9.3 — how a piece of evidence relates to its claim. */
export const EVIDENCE_POLARITY = ['supports', 'contradicts', 'qualifies', 'weakens'] as const;
export type EvidencePolarity = (typeof EVIDENCE_POLARITY)[number];

/** §10 — what kind of claim evidence is attached to. */
export const CLAIM_TYPES = [
  'decision',
  'transition',
  'key_result',
  'metric',
  'bet',
  'frame',
  'principle',
  'rule',
  'artifact_status',
  // --- Milestone A addition (DECISIONS-LOCKED): appended, never reorder the above ---
  'package_adoption',
] as const;
export type ClaimType = (typeof CLAIM_TYPES)[number];

/**
 * Claim lifecycle status. The legacy three (`active`, `resolved`, `withdrawn`)
 * stay FIRST for back-compat with existing TEXT rows; the accepted ladder
 * (technical/11 `claim.*` events / OQ-25 / K3) is APPENDED, never reordered.
 */
export const CLAIM_STATUS = [
  // --- v0.1 legacy (keep FIRST — index/value stability for existing rows) ---
  'active',
  'resolved',
  'withdrawn',
  // --- accepted ladder (technical/11 claim.* events / OQ-25; appended) ---
  'asserted',
  'review_requested',
  'accepted',
  'rejected',
  'contradicted',
  'superseded',
] as const;
export type ClaimStatus = (typeof CLAIM_STATUS)[number];

/** The claim-status value(s) that count a claim as accepted for readiness. */
export const CLAIM_ACCEPTED_STATUSES = ['accepted'] as const;
export type ClaimAcceptedStatus = (typeof CLAIM_ACCEPTED_STATUSES)[number];

/**
 * Decision Room readiness guard — true only when a claim sits at an
 * accepted-ladder accepted status. Legacy `active`/`resolved` are NOT accepted.
 */
export function isClaimAccepted(status: string): status is ClaimAcceptedStatus {
  return (CLAIM_ACCEPTED_STATUSES as readonly string[]).includes(status);
}

/** §8.2 — artifact crystallization lifecycle (observed → archived). */
export const CRYSTALLIZATION_STAGES = [
  'observed',
  'recorded',
  'classified',
  'enriched',
  'linked_to_context',
  'processed',
  'reviewed',
  'accepted',
  'rejected',
  'archived',
] as const;
export type CrystallizationStage = (typeof CRYSTALLIZATION_STAGES)[number];
